import { ApiEvent } from '@/api';
import { formatLocaleShort, proximityBucket, energyLevel, ensureSeconds, parseLocal } from '@/utils/datetime';
import { eventStartISO } from '@/utils/event';
import { FiCalendar, FiMapPin, FiClock } from 'react-icons/fi';
import { CountdownHero } from '@/components/CountdownHero';
import { useEffect, useMemo, useRef, useState } from 'react';

function ensureEnergyCSS() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('energy-css')) return;
  const css = `
  @media (prefers-reduced-motion: no-preference) {
    @keyframes energyShimmer {
      0%   { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
  }
  .energy-shimmer {
    position: absolute; inset: 0; border-radius: inherit; pointer-events: none;
    mix-blend-mode: screen;
    background:
      linear-gradient(120deg,
        rgba(255,255,255,0) 0%,
        rgba(255,255,255,0.06) 35%,
        rgba(255,255,255,0.22) 50%,
        rgba(255,255,255,0.06) 65%,
        rgba(255,255,255,0) 100%);
    background-size: 200% 100%;
    animation: energyShimmer var(--shimmerSpeed, 6s) linear infinite;
    opacity: 0;
  }
  .energy-tint {
    position: absolute; inset: -2px; border-radius: inherit; pointer-events: none;
    background:
      radial-gradient(120% 120% at 20% 0%,
        color-mix(in oklab, var(--accent-color), transparent 92%), transparent 60%),
      radial-gradient(140% 110% at 100% 100%,
        color-mix(in oklab, var(--accent-color), transparent 94%), transparent 60%);
    opacity: 0;
    transition: opacity 200ms ease;
  }
  `;
  const el = document.createElement('style');
  el.id = 'energy-css';
  el.textContent = css;
  document.head.appendChild(el);
}

export function LockedCard({
  ev,
  onICS,
  now
}: {
  ev: ApiEvent;
  onICS: () => void;
  now: Date;
}) {
  useEffect(() => { ensureEnergyCSS(); }, []);

  const start = eventStartISO(ev);
  const startIso = ensureSeconds(start);
  const friendly = formatLocaleShort(startIso);
  const prox = proximityBucket(startIso, now);

  // Visual energy 0..1 up to start (drops to 0 once past)
  const energy = useMemo(() => energyLevel(startIso, now), [startIso, now]);

  // expose a simple days-left integer for the micro-flare gate
  const daysLeft = useMemo(() => {
    const nowMs = now.getTime();
    const startMs = parseLocal(startIso).getTime();
    return Math.max(0, Math.ceil((startMs - nowMs) / 86400000));
  }, [startIso, now]);

  // once-per-(event,day) micro-flare
  const [flare, setFlare] = useState(false);
  const flareKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const key = `${ev.id}:d${daysLeft}`;
    if (flareKeyRef.current === key) return;
    flareKeyRef.current = key;
    if (typeof sessionStorage !== 'undefined' && daysLeft > 0 && !sessionStorage.getItem(key)) {
      sessionStorage.setItem(key, '1');
      setFlare(true);
      const t = setTimeout(() => setFlare(false), 950);
      return () => clearTimeout(t);
    }
  }, [daysLeft, ev.id]);

  const shimmerSpeed = (6 - 4 * energy).toFixed(2) + 's';
  const shimmerOpacity = 0.06 + 0.22 * energy;
  const tintOpacity = 0.05 + 0.25 * energy;
  const saturate = (1 + 0.45 * energy).toFixed(3);
  const brighten = (1 + 0.03 * energy).toFixed(3);

  return (
    <article
      className={`card glass card--event ${flare ? 'is-flare' : ''}`}
      role="listitem"
      aria-label={ev.title}
      data-prox={prox}
      data-energy={energy.toFixed(3)}
      style={{
        filter: `saturate(${saturate}) brightness(${brighten})`,
        ['--shimmerSpeed' as any]: shimmerSpeed,
        ['--progress' as any]: energy   // drives subtle progress effects
      }}
    >
      {flare && <div className="flare-layer" aria-hidden />}

      {energy > 0 ? (
        <>
          <div className="energy-shimmer" aria-hidden style={{ opacity: shimmerOpacity }} />
          <div className="energy-tint" aria-hidden style={{ opacity: tintOpacity }} />
        </>
      ) : null}

      <div className="card-fab">
        <button
          className="btn icon small"
          onClick={onICS}
          aria-label="Add to Calendar"
          title="Add to Calendar (.ics)"
        >
          <FiCalendar />
        </button>
      </div>

      <h3 className="card-title-center">{ev.title}</h3>

      {/* Countdown is ONLY until start; no "live" period */}
      <CountdownHero startIso={startIso} now={now} randSeed={ev.id} />

      <div className="card-meta-row">
        <div className="meta meta-left">
          <FiMapPin aria-hidden="true" />
          <span className="truncate">{ev.location || 'TBA'}</span>
        </div>
        <div className="meta meta-right" title={friendly}>
          <FiClock aria-hidden="true" />
          <span>{friendly}</span>
        </div>
      </div>
    </article>
  );
}
